import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Tables, Update, Json } from "@/types/database";
import { assertData, throwIfError } from "./_supabaseErrors";
import { createContact, deleteContact, listContacts } from "./contacts";
import { createEmailDraft } from "./emailDrafts";
import { createExpense, deleteExpense } from "./expenses";
import { createQuote, deleteQuote, getQuote, updateQuote } from "./quotes";
import { createQuoteLineItems } from "./quoteLineItems";
import {
  enrichExpenseAmounts,
  enrichQuoteAmounts,
  proposalBotExpenseSchema,
  proposalContactSchema,
  proposalQuoteSchema,
  proposalQuoteUpdateSchema,
  type BotProposalKind,
  type BotProposalPayload,
  type ExpenseVatBulkItem,
  type ProposalBotExpense,
  type ProposalContact,
  proposalSfrbMatchSchema,
  type ProposalDiaryEntry,
  type ProposalDocumentClassify,
  type ProposalEmailDraft,
  type ProposalQuote,
  type ProposalQuoteUpdate,
  type ProposalSfrbMatch,
  type ProposalTaskUpdate
} from "@/lib/ai/proposals/botIngestTypes";
import { appendToSiteDailyLog } from "./siteDailyLogs";
import { updateExpense } from "./expenses";
import { updateTask } from "./tasks";
import { updateDocument } from "./documents";
import { vatRateFromCode, type VatCode } from "@/lib/vat";
import { descriptionKey } from "@/lib/sfrb/text";
import { excludeTestData } from "./testData";
import { normalizeWriteSource, type WriteSource } from "./writeSource";

export type BotProposal = Tables<"bot_proposals">;
export type BotProposalStatus = BotProposal["status"];
export type BotProposalSource = BotProposal["source"];

export const BOT_PROPOSAL_KIND_LABEL: Record<BotProposalKind, string> = {
  quote: "Árajánlat",
  quote_update: "Árajánlat frissítés",
  expense: "Kiadás",
  contact: "Kontakt",
  task_update: "Feladat frissítés",
  document_classify: "Dokumentum osztályozás",
  sfrb_match: "ŠFRB párosítás",
  email_draft: "Hosszabbítás piszkozat",
  diary_entry: "Napló javaslat"
};

export const BOT_PROPOSAL_SOURCE_LABEL: Record<BotProposalSource, string> = {
  grok: "Grok Bot",
  claude: "Házépítés bot",
  ui: "Kézi",
  cron: "Ütemezett",
  bot: "Grok Bot" // legacy → 042 után grok
};

export const BOT_PROPOSAL_STATUS_LABEL: Record<BotProposalStatus, string> = {
  pending: "Jóváhagyásra vár",
  applied: "Alkalmazva",
  rejected: "Elutasítva",
  failed: "Sikertelen",
  expired: "Lejárt",
  undone: "Visszavonva"
};

export const REJECT_QUICK_REASONS = [
  "Duplikátum",
  "Hibás összeg",
  "Nem releváns",
  "Hiányos adatok"
] as const;

const UNDO_WINDOW_MS = 24 * 60 * 60 * 1000;

export type ListProposalsFilter = {
  status?: BotProposalStatus | BotProposalStatus[];
  kind?: BotProposalKind | BotProposalKind[];
  source?: BotProposalSource | BotProposalSource[];
};

async function currentUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

type ProposalUpdate = Update<"bot_proposals">;

/** 042 előtti sémán a bővített oszlopok hiányozhatnak — egyszer újrapróbál alap mezőkkel. */
async function updateProposalRow(
  id: string,
  values: ProposalUpdate,
  requirePending = false
): Promise<BotProposal> {
  let q = supabase.from("bot_proposals").update(values).eq("id", id);
  if (requirePending) q = q.eq("status", "pending");
  const first = await q.select("*").single();
  if (!first.error) return assertData(first.data);

  const msg = first.error.message ?? "";
  const missingCol =
    /column|schema cache|Could not find/i.test(msg) &&
    /(reject_reason|reviewed_by|reviewed_at|applied_refs|expires_at|content_hash|undone_at)/i.test(msg);
  if (!missingCol) {
    throwIfError(first.error);
  }

  const legacy: ProposalUpdate = { ...values };
  delete legacy.reject_reason;
  delete legacy.reviewed_by;
  delete legacy.reviewed_at;
  delete legacy.applied_refs;
  delete legacy.expires_at;
  delete legacy.content_hash;
  delete legacy.undone_at;
  let q2 = supabase.from("bot_proposals").update(legacy).eq("id", id);
  if (requirePending) q2 = q2.eq("status", "pending");
  const second = await q2.select("*").single();
  throwIfError(second.error);
  return assertData(second.data, "Javaslat nem található vagy már nincs függőben");
}

export async function listProposals(filter: ListProposalsFilter = {}): Promise<BotProposal[]> {
  let q = supabase.from("bot_proposals").select("*").order("created_at", { ascending: false });
  const statuses = filter.status
    ? Array.isArray(filter.status)
      ? filter.status
      : [filter.status]
    : undefined;
  const kinds = filter.kind ? (Array.isArray(filter.kind) ? filter.kind : [filter.kind]) : undefined;
  const sources = filter.source
    ? Array.isArray(filter.source)
      ? filter.source
      : [filter.source]
    : undefined;
  if (statuses?.length) q = q.in("status", statuses);
  if (kinds?.length) q = q.in("kind", kinds);
  if (sources?.length) q = q.in("source", sources);
  const { data, error } = await q.limit(200);
  throwIfError(error);
  return excludeTestData(data);
}

export async function listPendingBotProposals(): Promise<BotProposal[]> {
  return listProposals({ status: "pending" });
}

export async function countPendingBotProposals(): Promise<number> {
  const { count, error } = await supabase
    .from("bot_proposals")
    .select("id", { count: "exact", head: true })
    .eq("status", "pending");
  throwIfError(error);
  return count ?? 0;
}

export async function getBotProposal(id: string): Promise<BotProposal> {
  const { data, error } = await supabase.from("bot_proposals").select("*").eq("id", id).single();
  throwIfError(error);
  return assertData(data, "Javaslat nem található");
}

export function pendingBotProposalsQueryOptions() {
  return queryOptions({
    queryKey: ["botProposals", "pending"] as const,
    queryFn: listPendingBotProposals,
    refetchInterval: 60_000
  });
}

export function pendingBotProposalsCountQueryOptions() {
  return queryOptions({
    queryKey: ["botProposals", "pendingCount"] as const,
    queryFn: countPendingBotProposals,
    refetchInterval: 60_000
  });
}

export function botProposalsQueryOptions(filter: ListProposalsFilter = {}) {
  return queryOptions({
    queryKey: ["botProposals", "list", filter] as const,
    queryFn: () => listProposals(filter),
    refetchInterval: filter.status === "pending" || !filter.status ? 60_000 : false
  });
}

export function botProposalQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["botProposals", id] as const,
    queryFn: () => getBotProposal(id)
  });
}

export function parseProposalPayload(row: BotProposal): BotProposalPayload | null {
  const p = row.payload as Record<string, unknown> | null;
  if (!p || typeof p !== "object") return null;
  if (row.kind === "quote") {
    const parsed = proposalQuoteSchema.safeParse(p.quote ?? p);
    return parsed.success ? { kind: "quote", quote: enrichQuoteAmounts(parsed.data) } : null;
  }
  if (row.kind === "quote_update") {
    const parsed = proposalQuoteUpdateSchema.safeParse(p.quote_update ?? p);
    return parsed.success ? { kind: "quote_update", quote_update: parsed.data } : null;
  }
  if (row.kind === "expense") {
    const bulk = p.expense_vat_bulk;
    if (bulk && typeof bulk === "object" && Array.isArray((bulk as { items?: unknown }).items)) {
      const items = (bulk as { items: ExpenseVatBulkItem[] }).items.filter(
        (i) => i && typeof i.expense_id === "string" && typeof i.vat_code === "string"
      );
      if (items.length > 0) return { kind: "expense_vat_bulk", expense_vat_bulk: { items } };
    }
    const parsed = proposalBotExpenseSchema.safeParse(p.expense ?? p);
    return parsed.success ? { kind: "expense", expense: enrichExpenseAmounts(parsed.data) } : null;
  }
  if (row.kind === "contact") {
    const parsed = proposalContactSchema.safeParse(p.contact ?? p);
    return parsed.success ? { kind: "contact", contact: parsed.data } : null;
  }
  if (row.kind === "task_update") {
    const raw = (p.task_update ?? p) as ProposalTaskUpdate;
    if (raw && typeof raw.task_id === "string") {
      return { kind: "task_update", task_update: raw };
    }
    return null;
  }
  if (row.kind === "document_classify") {
    const raw = (p.document_classify ?? p) as ProposalDocumentClassify;
    if (raw && typeof raw.document_id === "string" && typeof raw.suggested_title === "string") {
      return { kind: "document_classify", document_classify: raw };
    }
    return null;
  }
  if (row.kind === "sfrb_match") {
    const parsed = proposalSfrbMatchSchema.safeParse(p.sfrb_match ?? p);
    return parsed.success ? { kind: "sfrb_match", sfrb_match: parsed.data } : null;
  }
  if (row.kind === "email_draft") {
    const raw = (p.email_draft ?? p) as ProposalEmailDraft;
    if (raw && typeof raw.to_address === "string" && typeof raw.subject === "string" && typeof raw.body === "string") {
      return { kind: "email_draft", email_draft: raw };
    }
    return null;
  }
  if (row.kind === "diary_entry") {
    const raw = (p.diary_entry ?? p) as ProposalDiaryEntry;
    if (raw && typeof raw.log_date === "string" && typeof raw.work_summary === "string" && raw.work_summary.trim()) {
      return { kind: "diary_entry", diary_entry: raw };
    }
    return null;
  }
  return null;
}

/** @deprecated használd a parseProposalPayload-ot */
export function botProposalToView(row: BotProposal): {
  kind: BotProposalKind;
  payload: BotProposalPayload | null;
} {
  return { kind: row.kind as BotProposalKind, payload: parseProposalPayload(row) };
}

export function canUndoProposal(row: BotProposal): boolean {
  if (row.status !== "applied" || !row.applied_at) return false;
  const appliedMs = new Date(row.applied_at).getTime();
  if (!Number.isFinite(appliedMs)) return false;
  if (Date.now() - appliedMs > UNDO_WINDOW_MS) return false;
  const refs = (row.applied_refs ?? row.result_ref) as Record<string, unknown> | null;
  return Boolean(refs && typeof refs === "object");
}

async function resolveContactId(quote: ProposalQuote): Promise<string | null> {
  if (quote.contact_id) return quote.contact_id;
  const email = quote.contact_email?.trim().toLowerCase();
  if (!email) return null;
  const contacts = await listContacts();
  const existing = contacts.find((c) => (c.email ?? "").toLowerCase() === email);
  if (existing) return existing.id;
  const created = await createContact({
    name: quote.contact_name?.trim() || email,
    email,
    type: "supplier",
    source_email: email,
    created_by: "bot"
  });
  return created.id;
}

async function applyQuote(quote: ProposalQuote, source: WriteSource = "ui"): Promise<Record<string, string>> {
  const enriched = enrichQuoteAmounts(quote);
  const contactId = await resolveContactId(enriched);
  const row = await createQuote({
    title: enriched.title,
    task_id: enriched.task_id ?? null,
    contact_id: contactId,
    amount: enriched.amount ?? enriched.amount_gross ?? null,
    currency: enriched.currency ?? "EUR",
    status: enriched.status ?? "pending",
    valid_until: enriched.valid_until ?? null,
    notes: enriched.notes ?? null,
    file_url: enriched.file_path ?? null,
    quote_number: enriched.quote_number ?? null,
    source_email_id: enriched.source_email_id ?? null,
    source_email_thread_id: enriched.source_email_thread_id ?? null,
    variant_group: enriched.variant_group ?? null,
    amount_net: enriched.amount_net ?? null,
    vat_rate: enriched.vat_rate ?? 23,
    amount_gross: enriched.amount_gross ?? null,
    shipping_cost: enriched.shipping_cost ?? null,
    unloading_cost: enriched.unloading_cost ?? null,
    pallet_deposit: enriched.pallet_deposit ?? null,
    pallet_amortization: enriched.pallet_amortization ?? null,
    comparable_total: enriched.comparable_total ?? null,
    is_comparable: enriched.is_comparable ?? true,
    comparability_note: enriched.comparability_note ?? null,
    delivery_window: enriched.delivery_window ?? null,
    rfq_id: enriched.rfq_id ?? null
  }, source);

  if (enriched.rfq_id) {
    const { linkQuoteToRfq } = await import("./rfqs");
    await linkQuoteToRfq(row.id, enriched.rfq_id, contactId);
  }

  const lines = enriched.line_items ?? [];
  if (lines.length > 0) {
    await createQuoteLineItems(
      lines.map((li, index) => ({
        quote_id: row.id,
        name: li.name,
        unit: li.unit ?? null,
        quantity: li.quantity ?? null,
        unit_price_net: li.unit_price_net ?? null,
        total_net: li.total_net ?? null,
        vat_rate: li.vat_rate ?? enriched.vat_rate ?? 23,
        total_gross: li.total_gross ?? null,
        item_type: li.item_type ?? "other",
        excluded_from_comparison: li.excluded_from_comparison ?? false,
        sort_index: li.sort_index ?? index
      }))
    );
  }
  if (row.status === "accepted") {
    try {
      const { ensureCommitmentFromAcceptedQuote } = await import("./commitments");
      await ensureCommitmentFromAcceptedQuote(row.id);
    } catch {
      /* best-effort — a quote már létrejött */
    }
  }
  return { quote_id: row.id };
}

async function applyQuoteUpdate(u: ProposalQuoteUpdate): Promise<Record<string, unknown>> {
  const before = await getQuote(u.quote_id);
  const patch: Parameters<typeof updateQuote>[1] = {};
  if (u.status) patch.status = u.status;
  if (u.valid_until !== undefined) patch.valid_until = u.valid_until ?? null;
  if (u.notes !== undefined) patch.notes = u.notes ?? null;
  await updateQuote(u.quote_id, patch);
  return {
    quote_id: u.quote_id,
    previous: {
      status: before.status,
      valid_until: before.valid_until,
      notes: before.notes
    }
  };
}

async function applyExpense(expense: ProposalBotExpense, source: WriteSource = "ui"): Promise<Record<string, string>> {
  const e = enrichExpenseAmounts(expense);
  const amount = e.amount ?? e.amount_gross;
  if (amount == null || !(amount > 0)) throw new Error("Hiányzó kiadás összeg.");
  const row = await createExpense({
    title: e.title,
    amount,
    date: e.date,
    category: e.category,
    phase_id: e.phase_id ?? null,
    task_id: e.task_id ?? null,
    paid_by: e.paid_by ?? null,
    notes: e.notes ?? null,
    receipt_url: e.receipt_path ?? null,
    amount_net: e.amount_net ?? null,
    vat_rate: e.vat_rate ?? 23,
    vat_code: e.vat_code ?? null,
    vat_amount:
      e.amount_gross != null && e.amount_net != null
        ? Math.round((e.amount_gross - e.amount_net) * 100) / 100
        : null,
    amount_basis: e.amount_basis === "net" || e.amount_basis === "black" ? e.amount_basis : "gross",
    amount_gross: e.amount_gross ?? amount,
    supplier_name: e.supplier_name ?? null,
    supplier_contact_id: e.supplier_contact_id ?? null,
    source_email_id: e.source_email_id ?? null,
    invoice_number: e.invoice_number ?? null,
    quote_id: e.quote_id ?? null
  }, source);
  return { expense_id: row.id };
}

async function applyExpenseVatBulk(items: ExpenseVatBulkItem[]): Promise<Record<string, unknown>> {
  const updated: string[] = [];
  for (const item of items) {
    const code = item.vat_code as VatCode;
    await updateExpense(item.expense_id, {
      vat_code: code,
      vat_rate: item.vat_rate ?? vatRateFromCode(code),
      amount_net: item.amount_net,
      vat_amount: item.vat_amount,
      amount_gross: item.amount_gross,
      amount: item.amount_gross
    });
    updated.push(item.expense_id);
  }
  return { expense_ids: updated, action: "vat_bulk" };
}

async function applyTaskUpdate(u: ProposalTaskUpdate): Promise<Record<string, unknown>> {
  const patch: {
    title?: string;
    budget_planned?: number | null;
    notes?: string | null;
  } = {};
  if (u.title != null && u.title.trim()) patch.title = u.title.trim();
  if (u.budget_planned !== undefined) patch.budget_planned = u.budget_planned;
  if (u.notes !== undefined) patch.notes = u.notes;
  await updateTask(u.task_id, patch);
  return { task_id: u.task_id, action: "task_update" };
}

async function applyDocumentClassify(d: ProposalDocumentClassify): Promise<Record<string, unknown>> {
  await updateDocument(d.document_id, {
    title: d.suggested_title,
    ...(d.category ? { category: d.category as never } : {})
  });
  return { document_id: d.document_id, action: "document_classify" };
}

async function applySfrbMatch(match: ProposalSfrbMatch): Promise<Record<string, unknown>> {
  const existing = await supabase
    .from("sfrb_invoice_lines")
    .select("id")
    .eq("expense_id", match.expense_id)
    .eq("line_no", match.line_no)
    .maybeSingle();
  throwIfError(existing.error);
  let lineId = existing.data?.id ?? null;
  if (!lineId) {
    const inserted = await supabase
      .from("sfrb_invoice_lines")
      .insert({
        expense_id: match.expense_id,
        line_no: match.line_no,
        description: match.invoice_description,
        quantity: match.invoice_qty ?? null,
        unit: match.invoice_unit ?? null,
        unit_price: match.invoice_unit_price ?? null,
        amount: match.invoice_amount ?? null,
        extract_source: "ai"
      })
      .select("id")
      .single();
    throwIfError(inserted.error);
    lineId = inserted.data?.id ?? null;
  }

  const now = new Date().toISOString();
  const created = await supabase
    .from("sfrb_matches")
    .insert({
      expense_id: match.expense_id,
      invoice_line_id: lineId,
      budget_item_id: match.budget_item_id ?? null,
      budget_code: match.budget_code,
      invoice_description: match.invoice_description,
      invoice_qty: match.invoice_qty ?? null,
      invoice_unit: match.invoice_unit ?? null,
      invoice_unit_price: match.invoice_unit_price ?? null,
      invoice_amount: match.invoice_amount ?? null,
      budget_qty: match.budget_qty ?? null,
      budget_unit: match.budget_unit ?? null,
      conversion_note: match.conversion_note ?? null,
      confidence: match.confidence ?? null,
      reason: match.reason || "ŠFRB párosítás jóváhagyva.",
      status: "confirmed",
      source: match.source,
      split_index: match.split_index,
      confirmed_at: now
    })
    .select("id")
    .single();
  throwIfError(created.error);

  const invoiceQty = match.invoice_qty ?? null;
  const budgetQty = match.budget_qty ?? null;
  const factor =
    invoiceQty && budgetQty != null && invoiceQty !== 0
      ? Math.round((budgetQty / invoiceQty) * 1_000_000) / 1_000_000
      : null;
  const memory = await supabase.from("sfrb_pair_memory").upsert(
    {
      description_key: descriptionKey(match.invoice_description),
      budget_code: match.budget_code,
      budget_item_id: match.budget_item_id ?? null,
      conversion_note: match.conversion_note ?? null,
      qty_factor: factor
    },
    { onConflict: "description_key,budget_code" }
  );
  throwIfError(memory.error);
  return { match_id: created.data?.id ?? null, expense_id: match.expense_id, action: "sfrb_match" };
}

async function applyContact(contact: ProposalContact, source: WriteSource = "ui"): Promise<Record<string, string>> {
  const email = (contact.source_email ?? contact.email)?.trim().toLowerCase() ?? null;
  if (email) {
    const contacts = await listContacts();
    const existing = contacts.find(
      (c) =>
        (c.source_email ?? "").toLowerCase() === email || (c.email ?? "").toLowerCase() === email
    );
    if (existing) return { contact_id: existing.id, existing: "true" };
  }
  const row = await createContact({
    name: contact.name,
    email: contact.email ?? email,
    phone: contact.phone ?? null,
    company: contact.company ?? null,
    type: contact.type ?? "supplier",
    trade: contact.trade ?? null,
    notes: contact.notes ?? null,
    source_email: email,
    created_by: "bot"
  }, source);
  return { contact_id: row.id };
}

async function applyDiaryEntry(entry: ProposalDiaryEntry): Promise<Record<string, string>> {
  const row = await appendToSiteDailyLog({
    logDate: entry.log_date,
    workSummary: entry.work_summary,
    ...(entry.issues ? { issues: entry.issues } : {})
  });
  return { daily_log_id: row.id };
}

async function applyEmailDraft(draft: ProposalEmailDraft): Promise<Record<string, string>> {
  const row = await createEmailDraft({
    to_address: draft.to_address,
    to_name: draft.to_name ?? null,
    subject: draft.subject,
    body: draft.body,
    lang: draft.lang ?? "sk",
    summary_hu: draft.summary_hu ?? null,
    purpose: draft.purpose ?? "follow_up",
    status: "pending_ok",
    contact_id: draft.contact_id ?? null
  });
  return { email_draft_id: row.id, sent: "false" };
}

function mergeEditedPayload(
  kind: BotProposalKind,
  base: BotProposalPayload,
  edited?: Record<string, unknown> | null
): BotProposalPayload {
  if (!edited) return base;
  if (kind === "quote" && base.kind === "quote") {
    const parsed = proposalQuoteSchema.safeParse({ ...base.quote, ...edited });
    if (!parsed.success) throw new Error("Érvénytelen szerkesztett ajánlat.");
    return { kind: "quote", quote: enrichQuoteAmounts(parsed.data) };
  }
  if (kind === "expense" && base.kind === "expense") {
    const parsed = proposalBotExpenseSchema.safeParse({ ...base.expense, ...edited });
    if (!parsed.success) throw new Error("Érvénytelen szerkesztett kiadás.");
    return { kind: "expense", expense: enrichExpenseAmounts(parsed.data) };
  }
  if (kind === "contact" && base.kind === "contact") {
    const parsed = proposalContactSchema.safeParse({ ...base.contact, ...edited });
    if (!parsed.success) throw new Error("Érvénytelen szerkesztett kontakt.");
    return { kind: "contact", contact: parsed.data };
  }
  if (kind === "quote_update" && base.kind === "quote_update") {
    const parsed = proposalQuoteUpdateSchema.safeParse({ ...base.quote_update, ...edited });
    if (!parsed.success) throw new Error("Érvénytelen szerkesztett frissítés.");
    return { kind: "quote_update", quote_update: parsed.data };
  }
  if (kind === "task_update" && base.kind === "task_update") {
    return {
      kind: "task_update",
      task_update: { ...base.task_update, ...(edited as Partial<ProposalTaskUpdate>) }
    };
  }
  if (kind === "document_classify" && base.kind === "document_classify") {
    return {
      kind: "document_classify",
      document_classify: {
        ...base.document_classify,
        ...(edited as Partial<ProposalDocumentClassify>)
      }
    };
  }
  if (kind === "sfrb_match" && base.kind === "sfrb_match") return base;
  if (kind === "email_draft" && base.kind === "email_draft") {
    return { kind: "email_draft", email_draft: { ...base.email_draft, ...(edited as Partial<ProposalEmailDraft>) } };
  }
  if (kind === "diary_entry" && base.kind === "diary_entry") {
    return { kind: "diary_entry", diary_entry: { ...base.diary_entry, ...(edited as Partial<ProposalDiaryEntry>) } };
  }
  return base;
}

function proposalMoneyAmount(payload: BotProposalPayload): number {
  if (payload.kind === "quote") {
    return Number(payload.quote.amount_gross ?? payload.quote.amount ?? 0) || 0;
  }
  if (payload.kind === "expense") {
    return Number(payload.expense.amount_gross ?? payload.expense.amount ?? 0) || 0;
  }
  // A párosítás nem új kiadás: ne akadjon el a kétfős összegküszöbön.
  if (payload.kind === "sfrb_match") return 0;
  return 0;
}

function editedKindBody(payload: Exclude<BotProposalPayload, { kind: "expense_vat_bulk" }>): unknown {
  switch (payload.kind) {
    case "quote":
      return payload.quote;
    case "quote_update":
      return payload.quote_update;
    case "expense":
      return payload.expense;
    case "contact":
      return payload.contact;
    case "task_update":
      return payload.task_update;
    case "document_classify":
      return payload.document_classify;
    case "sfrb_match":
      return payload.sfrb_match;
    case "email_draft":
      return payload.email_draft;
    case "diary_entry":
      return payload.diary_entry;
  }
}

/** F6.1 — nagy összegnél két különböző user szavazata kell. */
async function requireDualApprovalIfNeeded(
  proposalId: string,
  userId: string | null,
  amount: number
): Promise<"ready" | "waiting"> {
  const { data: project } = await supabase.from("project").select("*").limit(1).maybeSingle();
  const threshold = Number(
    (project as { dual_approval_threshold_eur?: number } | null)?.dual_approval_threshold_eur ?? 2000
  );
  if (!(amount > threshold) || !userId) return "ready";

  await supabase.from("proposal_approvals").upsert(
    { proposal_id: proposalId, user_id: userId },
    { onConflict: "proposal_id,user_id" }
  );
  const { data: votes } = await supabase
    .from("proposal_approvals")
    .select("user_id")
    .eq("proposal_id", proposalId);
  const unique = new Set((votes ?? []).map((v) => v.user_id));
  if (unique.size < 2) return "waiting";
  return "ready";
}

export async function approveProposal(
  id: string,
  editedPayload?: Record<string, unknown> | null
): Promise<BotProposal> {
  const row = await getBotProposal(id);
  if (row.status !== "pending") throw new Error("A javaslat már nincs függőben.");
  const parsed = parseProposalPayload(row);
  if (!parsed) throw new Error("Érvénytelen javaslat tartalom.");

  const userId = await currentUserId();
  const reviewedAt = new Date().toISOString();
  let effective = mergeEditedPayload(row.kind as BotProposalKind, parsed, editedPayload);

  const dual = await requireDualApprovalIfNeeded(id, userId, proposalMoneyAmount(effective));
  if (dual === "waiting") {
    throw new Error(
      "Nagy összeg (≥ küszöb): kell még egy másik családtag jóváhagyása. A szavazatod rögzítve."
    );
  }

  try {
    const writeSource = normalizeWriteSource(row.source);
    let refs: Record<string, unknown>;
    if (effective.kind === "quote") refs = await applyQuote(effective.quote, writeSource);
    else if (effective.kind === "quote_update") refs = await applyQuoteUpdate(effective.quote_update);
    else if (effective.kind === "expense") refs = await applyExpense(effective.expense, writeSource);
    else if (effective.kind === "expense_vat_bulk") refs = await applyExpenseVatBulk(effective.expense_vat_bulk.items);
    else if (effective.kind === "task_update") refs = await applyTaskUpdate(effective.task_update);
    else if (effective.kind === "document_classify") refs = await applyDocumentClassify(effective.document_classify);
    else if (effective.kind === "sfrb_match") refs = await applySfrbMatch(effective.sfrb_match);
    else if (effective.kind === "email_draft") refs = await applyEmailDraft(effective.email_draft);
    else if (effective.kind === "diary_entry") refs = await applyDiaryEntry(effective.diary_entry);
    else refs = await applyContact(effective.contact, writeSource);

    const payloadUpdate =
      editedPayload != null && effective.kind !== "expense_vat_bulk"
        ? ({
            ...((row.payload as Record<string, unknown>) ?? {}),
            [effective.kind]: editedKindBody(effective)
          } as Json)
        : undefined;

    return await updateProposalRow(
      id,
      {
        status: "applied",
        applied_at: reviewedAt,
        reviewed_at: reviewedAt,
        reviewed_by: userId,
        applied_refs: refs as Json,
        result_ref: refs as Json,
        error_message: null,
        ...(payloadUpdate != null ? { payload: payloadUpdate } : {})
      },
      true
    );
  } catch (e) {
    const message = e instanceof Error ? e.message : "Alkalmazás sikertelen";
    // 042 után failed; előtte pending marad + error_message
    try {
      await updateProposalRow(
        id,
        {
          status: "failed",
          error_message: message,
          reviewed_at: reviewedAt,
          reviewed_by: userId
        },
        true
      );
    } catch {
      try {
        await updateProposalRow(id, { error_message: message }, true);
      } catch {
        /* best-effort */
      }
    }
    throw e instanceof Error ? e : new Error(message);
  }
}

/** @deprecated használd az approveProposal-t */
export async function applyBotProposal(id: string): Promise<BotProposal> {
  return approveProposal(id);
}

export async function rejectProposal(id: string, reason: string): Promise<BotProposal> {
  const trimmed = reason.trim();
  if (!trimmed) throw new Error("Az elutasítás indoka kötelező.");
  const userId = await currentUserId();
  return updateProposalRow(
    id,
    {
      status: "rejected",
      rejected_at: new Date().toISOString(),
      reviewed_at: new Date().toISOString(),
      reviewed_by: userId,
      reject_reason: trimmed,
      // 042 előtt: indok az error_message-be is
      error_message: trimmed
    },
    true
  );
}

/** @deprecated használd a rejectProposal-t */
export async function rejectBotProposal(id: string, reason = "Elutasítva"): Promise<BotProposal> {
  return rejectProposal(id, reason);
}

export async function bulkApprove(ids: string[]): Promise<{ ok: string[]; failed: Array<{ id: string; error: string }> }> {
  const ok: string[] = [];
  const failed: Array<{ id: string; error: string }> = [];
  for (const id of ids) {
    try {
      await approveProposal(id);
      ok.push(id);
    } catch (e) {
      failed.push({ id, error: e instanceof Error ? e.message : "Hiba" });
    }
  }
  return { ok, failed };
}

export async function undoProposal(id: string): Promise<BotProposal> {
  const row = await getBotProposal(id);
  if (!canUndoProposal(row)) {
    throw new Error("A visszavonás csak 24 órán belül, alkalmazott javaslatnál érhető el.");
  }
  const refs = (row.applied_refs ?? row.result_ref) as Record<string, unknown>;

  if (row.kind === "quote" && typeof refs.quote_id === "string") {
    await deleteQuote(refs.quote_id);
  } else if (row.kind === "expense" && typeof refs.expense_id === "string") {
    await deleteExpense(refs.expense_id);
  } else if (row.kind === "contact" && typeof refs.contact_id === "string" && refs.existing !== "true") {
    await deleteContact(refs.contact_id);
  } else if (row.kind === "sfrb_match" && typeof refs.match_id === "string") {
    const { error } = await supabase.from("sfrb_matches").delete().eq("id", refs.match_id);
    throwIfError(error);
  } else if (row.kind === "quote_update" && typeof refs.quote_id === "string" && refs.previous) {
    const prev = refs.previous as { status?: string; valid_until?: string | null; notes?: string | null };
    await updateQuote(refs.quote_id, {
      status: (prev.status as "pending" | "accepted" | "rejected" | "expired") ?? undefined,
      valid_until: prev.valid_until ?? null,
      notes: prev.notes ?? null
    });
  } else {
    throw new Error("Nincs visszavonható hivatkozás.");
  }

  const userId = await currentUserId();
  const now = new Date().toISOString();
  // undone státusz 042 után; előtte rejected + megjegyzés
  let q = supabase
    .from("bot_proposals")
    .update({
      status: "undone",
      undone_at: now,
      reviewed_by: userId,
      reviewed_at: now
    })
    .eq("id", id)
    .eq("status", "applied");
  let res = await q.select("*").single();
  if (!res.error) return assertData(res.data);

  res = await supabase
    .from("bot_proposals")
    .update({
      status: "rejected",
      rejected_at: now,
      error_message: "Visszavonva (undo)"
    })
    .eq("id", id)
    .eq("status", "applied")
    .select("*")
    .single();
  throwIfError(res.error);
  return assertData(res.data);
}
